import { useEffect, useState } from "react";

// Dev-only debugging aid: draws two vertical guide lines pinned to the nav
// bar's own live left/right edges (measured from the real rendered element,
// not a hardcoded number), so every section's alignment can be checked by
// eye against the nav — the same way Figma's Shift+G layout-grid guides
// work, but live on the actual rendered page. Toggle with the ` key.
const AlignmentGuide = () => {
  const [visible, setVisible] = useState(false);
  const [edges, setEdges] = useState(null);

  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;

    let observer;

    const update = () => {
      const nav = document.querySelector('[data-testid="main-navbar"]');
      if (!nav) return;
      const rect = nav.getBoundingClientRect();
      // Round to whole pixels — a sub-pixel position (e.g. 348.5px) on a
      // 1px-wide line makes the browser anti-alias it across two pixels at
      // half opacity each, which reads as basically invisible.
      // Derive both lines from rect.left/rect.right directly (both already
      // viewport-relative, the same coordinate space `position: fixed` uses)
      // rather than `window.innerWidth - rect.right` — innerWidth includes
      // the scrollbar's width while getBoundingClientRect() doesn't, so that
      // math silently shifted the right line inward by the scrollbar's width.
      setEdges({ left: Math.round(rect.left), right: Math.round(rect.right) });

      // A plain window "resize" listener misses layout changes that aren't
      // caused by resizing the window — e.g. hot-reloading a component that
      // changes the nav's own width leaves the lines pinned to the nav's
      // *previous* position until something happens to fire a resize event.
      // Watch the nav element itself so the lines always track its current
      // real layout, not a stale snapshot.
      if (!observer) {
        observer = new ResizeObserver(update);
        observer.observe(nav);
      }
    };

    const onKey = (e) => {
      if (e.key === "`") setVisible((v) => !v);
    };

    update();
    window.addEventListener("resize", update);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("keydown", onKey);
      if (observer) observer.disconnect();
    };
  }, []);

  if (process.env.NODE_ENV !== "development" || !visible || !edges) return null;

  const lineStyle = {
    position: "fixed",
    top: 0,
    bottom: 0,
    width: "2px",
    background: "#FFD600",
    boxShadow: "0 0 6px 1px rgba(255, 214, 0, 0.9)",
    zIndex: 9999,
    pointerEvents: "none",
  };

  // A 12-column grid between the two edge lines, matching the grid-cols-12
  // already used across sections (WhatWeDo, Footer, FAQSection, etc.) — thinner
  // and dimmer than the edges so those two stay the primary reference.
  const COLUMNS = 12;
  const colStyle = {
    position: "fixed",
    top: 0,
    bottom: 0,
    width: "1px",
    background: "rgba(255, 214, 0, 0.35)",
    zIndex: 9998,
    pointerEvents: "none",
  };
  const width = edges.right - edges.left;
  const columnLines = Array.from({ length: COLUMNS - 1 }, (_, i) =>
    Math.round(edges.left + ((i + 1) / COLUMNS) * width)
  );

  return (
    <>
      {columnLines.map((x) => (
        <div key={x} style={{ ...colStyle, left: x }} />
      ))}
      <div style={{ ...lineStyle, left: edges.left }} />
      <div style={{ ...lineStyle, left: edges.right }} />
      <div
        style={{
          position: "fixed",
          bottom: 12,
          left: "50%",
          transform: "translateX(-50%)",
          background: "rgba(17,17,17,0.85)",
          color: "#fff",
          fontSize: 12,
          padding: "4px 10px",
          borderRadius: 999,
          zIndex: 9999,
          pointerEvents: "none",
        }}
      >
        Alignment guides on — press ` to hide
      </div>
    </>
  );
};

export default AlignmentGuide;
